import React, { useState, useEffect, useRef, useMemo} from 'react';
 
const ImageComponent = ({ imageData, isLoading, contentId, subtitle, footer, isEditing = false, onFieldChange, hideContentLabel = false })  => {
  const containerRef = useRef(null);
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.getAttribute('data-theme') === 'dark'
  );
  const [zoomLevel, setZoomLevel] = useState(window.devicePixelRatio || 1);

  const processDocTags = (text) => {
    if (!text) return text;
    const maxLen = 15;
    let processed = text.replace(
      /<span([^>]*\b(?:ev-message-card6|ev-message-card7)\b[^>]*)>([\s\S]*?)<\/span>/g,
      (full, attrs, inner) => {
        const rawText = (inner || '').trim();
        if (rawText.includes('<')) return full;
        const shortText = rawText.length <= maxLen ? rawText : rawText.slice(0, maxLen - 1) + '…';
        const hasTitle = /\stitle=/.test(attrs);
        const safeTitle = rawText.replace(/"/g, '&quot;');
        const newAttrs = hasTitle ? attrs : `${attrs} title="${safeTitle}"`;
        return `<span${newAttrs}>${shortText}</span>`;
      }
    );
    // Convert JSX className to HTML class for dangerouslySetInnerHTML
    processed = processed.replace(/\bclassName=/g, 'class=');
    return processed;
  };

  const processedSubtitle = useMemo(() => processDocTags(subtitle), [subtitle]);
  const processedFooter = useMemo(() => processDocTags(footer), [footer]);
  const getCSSVariable = (variable) => {
    return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
  };
 
  // Get font size from CSS class
  const getFontSize = (className) => {
    const tempElement = document.createElement('span');
    tempElement.className = className;
    tempElement.style.visibility = 'hidden';
    document.body.appendChild(tempElement);
    const fontSize = parseFloat(getComputedStyle(tempElement).fontSize);
    document.body.removeChild(tempElement);
    return fontSize;
  };
 
  const getMargin = (className) => {
    const tempElement = document.createElement('span');
    tempElement.className = className;
    tempElement.style.visibility = 'hidden';
    document.body.appendChild(tempElement);
    const margin = parseFloat(getComputedStyle(tempElement).marginLeft);
    document.body.removeChild(tempElement);
    return margin;
  };
 
  // Get font weight from CSS class
  const getFontWeight = (className) => {
    const tempElement = document.createElement('span');
    tempElement.className = className;
    tempElement.style.visibility = 'hidden';
    document.body.appendChild(tempElement);
    const fontWeight = getComputedStyle(tempElement).fontWeight;
    document.body.removeChild(tempElement);
    return fontWeight;
  };
 
  // Get font family from CSS class
  const getFontFamily = (className) => {
    const tempElement = document.createElement('span');
    tempElement.className = className;
    tempElement.style.visibility = 'hidden';
    document.body.appendChild(tempElement);
    const fontFamily = getComputedStyle(tempElement).fontFamily;
    document.body.removeChild(tempElement);
    return fontFamily;
  };
 
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          const newTheme = document.documentElement.getAttribute('data-theme');
          setIsDarkMode(newTheme === 'dark');
        }
      });
    });
 
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
 
    return () => observer.disconnect();
  }, []);
 
  useEffect(() => {
    const handleResize = () => {
      setZoomLevel(window.devicePixelRatio || 1);
    };
 
    window.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('resize', handleResize);
    };
  }, []);
 
  useEffect(() => {
    const styleId = 'image-container-styles';
    
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .image-display-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 100%;
          height: 50vh;
          padding: var(--px-16, 16px);
          background-color: var(--background2);
          border: 1px solid var(--color12);
          border-radius: 4px;
          box-sizing: border-box;
        }
        
        .image-display-wrapper img {
          max-width: 100%;
          max-height: 100%;
          height: auto;
          display: block;
          border-radius: 4px;
        }
      `;
      document.head.appendChild(style);
    }
    
    return () => {
      const styleElement = document.getElementById(styleId);
      if (styleElement) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);
 
  if (isLoading) {
    return (
      <div className="graph-container align-items-center justify-content-center d-flex ">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <br />
          <span className="ev-reg-p-dark">
            Loading Image
          </span>
        </div>
      </div>
    );
  }
 
  if (!imageData || (!imageData.presigned_url && !imageData.bytes_base64)) {
    return (
      <div className="container-error">
        <div className="text-center">
          <p>Error loading image</p>
        </div>
      </div>
    );
  }
 
  const src = imageData.presigned_url? imageData.presigned_url : `data:${imageData.content_type || 'image/png'};base64,${imageData.bytes_base64}`;
 
  // Calculate theme colors
  const themeColors = {
    paper_bgcolor: getCSSVariable('--background2'),
    plot_bgcolor: getCSSVariable('--background2'),
    fontColor: getCSSVariable('--color4'),  
    gridColor: getCSSVariable('--color12'),
    titleColor: getCSSVariable('--color2'),
  };
 
  // Get font sizes from CSS classes
  const fontSize = getFontSize('ev-fs-11');
  const mainTitleSize = getFontSize('ev-fs-13');
 
  // Get font weights from CSS classes
  const mainTitleWeight = getFontWeight('ev-bold-med');
 
  // Get font family from CSS class
  const fontFamily = getFontFamily('poppinsfont');
 
  return (
    <div className="graph-container">
      {(isEditing || (subtitle && !hideContentLabel)) && (
        <div className="mb-2">
          {isEditing ? (
            <div className="genbi-figure-edit-field">
              <span className="ev-fs-11 ev-small-p-blue ev-bold-med mb-1 d-inline-block">
                <i className="bi bi-image"></i> <b>Figure {contentId} — Subtitle</b>
              </span>
              <textarea
                className="formField2 ev-fs-11 w-100 genbi-footer-textarea"
                value={subtitle || ''}
                onChange={(e) => onFieldChange?.('subtitle', e.target.value)}
                rows={2}
                placeholder="Edit subtitle..."
              />
            </div>
          ) : (
            <span className="ev-message-card2 w-100 m-0 ev-fs-11 text-wrap">
              <i className="bi bi-image"></i> <b>Figure {contentId}. </b> <span dangerouslySetInnerHTML={{ __html: processedSubtitle }} />
            </span>
          )}
        </div>
      )}
      <div
        ref={containerRef}
        className="image-display-wrapper"
        style={{
          backgroundColor: themeColors.paper_bgcolor,
          borderColor: themeColors.gridColor,
        }}
        key={`${isDarkMode ? 'dark' : 'light'}-${zoomLevel}`}
      >
        <img
          src={src}
          alt={subtitle || `Figure ${contentId}`}
        />
      </div>
            {(isEditing || footer) && (
        <div className="mb-2">
          {isEditing ? (
            <div className="genbi-figure-edit-field">
              <span className="ev-fs-11 ev-small-p-blue ev-bold-med mb-1 d-inline-block">
                <i className="bi bi-pencil-sticky"></i> <b>Note</b>
              </span>
              <textarea
                className="formField2 ev-fs-11 w-100 genbi-footer-textarea"
                value={footer || ''}
                onChange={(e) => onFieldChange?.('footer', e.target.value)}
                rows={2}
                placeholder="Edit footnote..."
              />
            </div>
          ) : (
            <span className="ev-message-card2 w-100 m-0 ev-fs-11 text-wrap">
              <i className="bi bi-pencil-sticky"></i>  <b>Note </b><span dangerouslySetInnerHTML={{ __html: processedFooter }} />
            </span>
          )}
        </div>
      )}
    </div>
  );
};
 
export default ImageComponent;